import React, { createContext, useContext, useEffect, useRef } from 'react';

import config from 'config';
import { useAuth } from 'context/auth-context';
import mixpanel from 'mixpanel-browser';

export interface MixpanelContextType {
  track: (eventName: string, properties?: Record<string, any>) => void;
  identify: (userId: string) => void;
  reset: () => void;
  setUserProperties: (properties: Record<string, any>) => void;
  startSessionRecording: () => void;
}

// Exported so Storybook can supply a no-op value: the filter bar's tracking
// hook runs on every render, and stories must never reach the real provider.
export const MixpanelContext = createContext<MixpanelContextType | undefined>(undefined);

interface MixpanelProviderProps {
  children: React.ReactNode;
}

export const MixpanelProvider = ({ children }: MixpanelProviderProps): JSX.Element => {
  const { session } = useAuth();
  const isInitialized = useRef(false);
  const isMixpanelAvailable = useRef(false);

  useEffect(() => {
    // Initialize Mixpanel only once with error handling
    if (!isInitialized.current && config.MIXPANEL_TOKEN) {
      try {
        mixpanel.init(config.MIXPANEL_TOKEN, {
          debug: config.ENVIRONMENT === 'local',
          track_pageview: false,
          persistence: 'localStorage',
          record_sessions_percent: 1,
          record_heatmap_data: true,
          autocapture: false,
        });
        isMixpanelAvailable.current = true;
        isInitialized.current = true;
      } catch (error) {
        console.warn('Mixpanel initialization failed:', error);
        isMixpanelAvailable.current = false;
        isInitialized.current = true; // Mark as initialized to prevent retries
      }
    }
  }, []);

  useEffect(() => {
    if (session?.user?.userId && isInitialized.current && isMixpanelAvailable.current) {
      try {
        mixpanel.identify(session.user.userId);

        const userProperties: Record<string, any> = {
          $email: session.user.email,
          $name: session.user.email,
          role: session.role,
          gym_id: session.gym?.gymId,
          gym_title: session.gym?.title,
          profile_id: session.profile?.profileId,
          environment: config.ENVIRONMENT,
        };

        // Add role-specific properties
        if (session.role === 'athlete') {
          userProperties.athlete_id = session.athlete?.athleteId;
        } else if (session.role === 'staff' || session.role === 'owner') {
          userProperties.staff_id = session.staff?.staffId;
        } else if (session.role === 'kilo_staff') {
          userProperties.kilo_staff_id = session.kiloStaff?.kiloStaffId;
          userProperties.is_sales_team = session.kiloStaff?.isSalesTeam;
        }

        mixpanel.people.set(userProperties);
      } catch (error) {
        console.warn('Mixpanel identify/setUserProperties failed:', error);
        isMixpanelAvailable.current = false;
      }
    }
  }, [session]);

  const track = (eventName: string, properties: Record<string, any> = {}): void => {
    if (!isInitialized.current || !isMixpanelAvailable.current) return;

    try {
      const eventProperties = {
        ...properties,
        timestamp: new Date().toISOString(),
        environment: config.ENVIRONMENT,
        role: session?.role,
        gym_id: session?.gym?.gymId,
        profile_id: session?.profile?.profileId,
      };

      mixpanel.track(eventName, eventProperties);
    } catch (error) {
      console.warn('Mixpanel track failed:', error, eventName);
      isMixpanelAvailable.current = false;
    }
  };

  const identify = (userId: string): void => {
    if (!isInitialized.current || !isMixpanelAvailable.current) return;

    try {
      mixpanel.identify(userId);
    } catch (error) {
      console.warn('Mixpanel identify failed:', error);
      isMixpanelAvailable.current = false;
    }
  };

  const reset = (): void => {
    if (!isInitialized.current || !isMixpanelAvailable.current) return;

    try {
      mixpanel.reset();
    } catch (error) {
      console.warn('Mixpanel reset failed:', error);
      isMixpanelAvailable.current = false;
    }
  };

  const setUserProperties = (properties: Record<string, any>): void => {
    if (!isInitialized.current || !isMixpanelAvailable.current) return;

    try {
      mixpanel.people.set(properties);
    } catch (error) {
      console.warn('Mixpanel setUserProperties failed:', error);
      isMixpanelAvailable.current = false;
    }
  };

  const startSessionRecording = (): void => {
    if (!isInitialized.current || !isMixpanelAvailable.current) return;

    try {
      mixpanel.start_session_recording();
    } catch (error) {
      console.warn('Mixpanel start_session_recording failed:', error);
    }
  };

  const value: MixpanelContextType = {
    track,
    identify,
    reset,
    setUserProperties,
    startSessionRecording,
  };

  return <MixpanelContext.Provider value={value}>{children}</MixpanelContext.Provider>;
};

export const useMixpanel = (): MixpanelContextType => {
  const context = useContext(MixpanelContext);
  if (context === undefined) {
    throw new Error('useMixpanel must be used within a MixpanelProvider');
  }
  return context;
};
